import type { SyncResponse } from '../shared/types.ts';
import { getMeta, setMeta, type LocalDeck, type LocalNote, type LocalReview } from './db.ts';
import type { Model } from './model.ts';
import { language } from '../shared/languages.ts';

// One round trip pushes everything dirty and pulls everything newer than the
// cursor; the server answers with the merged result, so applying the response
// is all the conflict handling a client does. Then audio: recordings made here
// go up, and any clip a note refers to that isn't on this device comes down.

class Unauthorized extends Error {}

export class Syncer {
  private model: Model;
  private inflight: Promise<void> | null = null;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private followUps = 0;

  constructor(model: Model) {
    this.model = model;
  }

  /** Sync within `delayMs`, coalescing with any sync already scheduled sooner. */
  schedule(delayMs: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run();
    }, delayMs);
  }

  run(): Promise<void> {
    if (this.inflight) {
      this.again = true;
      return this.inflight;
    }
    this.inflight = this.once().finally(() => {
      this.inflight = null;
      if (this.again) {
        this.again = false;
        void this.run();
      }
    });
    return this.inflight;
  }

  private async fetch(path: string, init: RequestInit = {}): Promise<Response> {
    const res = await fetch(path, {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${this.model.token}` },
    });
    if (res.status === 401) throw new Unauthorized('The server rejected this device\'s token.');
    return res;
  }

  private async once(): Promise<void> {
    const m = this.model;
    if (!m.token) {
      m.sync.status = 'signed-out';
      m.changed();
      return;
    }
    m.sync.status = 'syncing';
    m.sync.error = null;
    m.changed();
    try {
      await this.uploadRecordings();
      await this.exchange();
      await this.downloadAudio();
      m.sync.status = 'idle';
      m.sync.lastSyncAt = Date.now();
      await setMeta(m.db, 'lastSyncAt', m.sync.lastSyncAt);
      this.scheduleFollowUp();
    } catch (e) {
      if (e instanceof Unauthorized) {
        m.sync.status = 'signed-out';
        m.sync.error = e.message;
      } else if (!navigator.onLine || e instanceof TypeError) {
        // fetch rejects with TypeError when the network is unreachable.
        m.sync.status = 'offline';
      } else {
        m.sync.status = 'error';
        m.sync.error = e instanceof Error ? e.message : String(e);
        console.error('sync failed', e);
      }
    } finally {
      await m.countPending();
      m.changed();
    }
  }

  /**
   * A note with characters but no audio yet is waiting on the server's TTS,
   * which takes a few seconds per voice. Look again shortly, a few times.
   */
  private scheduleFollowUp(): void {
    const waiting = this.model
      .liveNotes()
      .some((n) => this.model.deckOf(n)?.active !== false && n.text.trim() && n.tts.length < language(this.model.deckOf(n)?.language ?? 'none').voices.length);
    if (waiting && this.followUps < 5) {
      this.followUps++;
      this.schedule(8000);
    } else if (!waiting) {
      this.followUps = 0;
    }
  }

  private async uploadRecordings(): Promise<void> {
    const db = this.model.db;
    for (const row of await db.getAllFromIndex('audio', 'uploaded', 0)) {
      const res = await this.fetch(`/api/audio/${row.hash}`, {
        method: 'PUT',
        headers: { 'Content-Type': row.mime },
        body: row.blob,
      });
      if (!res.ok) throw new Error(`audio upload failed: ${res.status}`);
      await db.put('audio', { ...row, uploaded: 1 });
    }
  }

  private async exchange(): Promise<void> {
    const m = this.model;
    const db = m.db;
    const pushDecks = await db.getAllFromIndex('decks', 'dirty', 1);
    const pushNotes = await db.getAllFromIndex('notes', 'dirty', 1);
    const pushReviews = await db.getAllFromIndex('reviews', 'dirty', 1);
    let since = await getMeta<number>(db, 'cursor', 0);
    let first = true;

    for (;;) {
      const body = {
        since,
        decks: first ? pushDecks.map(strip) : [],
        notes: first ? pushNotes.map(strip) : [],
        reviews: first ? pushReviews.map(strip) : [],
      };
      const res = await this.fetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`sync failed: ${res.status} ${await res.text()}`);
      const data = (await res.json()) as SyncResponse;

      const tx = db.transaction(['decks', 'notes', 'reviews', 'meta'], 'readwrite');
      const decks = tx.objectStore('decks');
      const notes = tx.objectStore('notes');
      const reviews = tx.objectStore('reviews');

      if (first) {
        // Acknowledge what was pushed, unless it changed again while in flight.
        for (const d of pushDecks) {
          const cur = await decks.get(d.id);
          if (cur && cur.dirty && cur.updatedAt === d.updatedAt) await decks.put({ ...cur, dirty: 0 });
        }
        for (const n of pushNotes) {
          const cur = await notes.get(n.id);
          if (cur && cur.dirty && cur.updatedAt === n.updatedAt) await notes.put({ ...cur, dirty: 0 });
        }
        for (const r of pushReviews) {
          const cur = await reviews.get(r.id);
          if (cur && cur.dirty && cur.deleted === r.deleted) await reviews.put({ ...cur, dirty: 0 });
        }
      }

      const appliedDecks: LocalDeck[] = [];
      for (const d of data.decks) {
        const cur = await decks.get(d.id);
        const row: LocalDeck = cur?.dirty && cur.updatedAt > d.updatedAt ? cur : { ...d, dirty: 0 };
        await decks.put(row);
        appliedDecks.push(row);
      }
      const appliedNotes: LocalNote[] = [];
      const appliedReviews: LocalReview[] = [];
      for (const n of data.notes) {
        const cur = await notes.get(n.id);
        let row: LocalNote;
        if (cur?.dirty && cur.updatedAt > n.updatedAt) {
          // Edited here mid-sync: keep ours (it goes up next time), but take the server's audio.
          row = cur.text.trim() === n.text.trim() && cur.deckId === n.deckId ? { ...cur, tts: n.tts } : cur;
        } else {
          row = { ...n, dirty: 0 };
        }
        await notes.put(row);
        appliedNotes.push(row);
      }
      for (const r of data.reviews) {
        const cur = await reviews.get(r.id);
        const deleted = r.deleted || (cur?.deleted ?? false);
        const row: LocalReview = { ...r, deleted, dirty: cur?.dirty && deleted !== r.deleted ? 1 : 0 };
        await reviews.put(row);
        appliedReviews.push(row);
      }
      await tx.objectStore('meta').put(data.cursor, 'cursor');
      await tx.done;

      m.applyPulled(appliedDecks, appliedNotes, appliedReviews);
      m.changed();
      since = data.cursor;
      first = false;
      if (!data.more) break;
    }
  }

  private async downloadAudio(): Promise<void> {
    const db = this.model.db;
    const have = new Set(await db.getAllKeys('audio'));
    const want = new Set<string>();
    // Only decks you study: a switched-off deck can be thousands of cards.
    for (const n of this.model.liveNotes()) {
      if (this.model.deckOf(n)?.active === false) continue;
      for (const h of n.recordings) want.add(h);
      for (const c of n.tts) want.add(c.hash);
    }
    const missing = [...want].filter((h) => !have.has(h));
    const worker = async () => {
      for (let h = missing.pop(); h; h = missing.pop()) {
        const res = await this.fetch(`/api/audio/${h}`);
        if (res.status === 404) continue; // a recording another device hasn't uploaded yet
        if (!res.ok) throw new Error(`audio download failed: ${res.status}`);
        const blob = await res.blob();
        await db.put('audio', { hash: h, blob, mime: blob.type || 'audio/mpeg', uploaded: 1 });
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
  }
}

function strip<T extends { dirty: 0 | 1 }>(row: T): Omit<T, 'dirty'> {
  const { dirty: _, ...rest } = row;
  return rest;
}
